import { ApiEnvelope } from './migrations';
import { getJson, postJsonWithStatus } from './client';

// --- Migration groups (FD-259 – FD-261 / FD-ADR-030) --------------------------
//
// A migration group is an operator-named container migrations belong to, for
// ORGANISATION AND STATUS ROLLUP ONLY. It carries no defaults, triggers no bulk
// operations, and no execution path reads it (FD-ADR-030 §3) — assigning a group
// changes nothing about how any device migrates.

export interface MigrationGroup {
  migrationGroupId: string;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

export interface GroupDestination {
  tenantId: string;
  tenantDomain: string | null;
  count: number;
}

// Every keyed sub-object arrives pre-seeded server-side, so the shape is
// identical at zero migrations and unrecognised values are counted rather than
// dropped. Consumers can therefore read `byWave.Pilot` without guarding for a
// missing key — but the maps are open-ended, so iterate rather than enumerate.
export interface MigrationGroupRollup {
  migrationCount: number;
  deviceCount: number;
  byWave: Record<string, number>;
  byType: Record<string, number>;
  byBprtStatus: Record<string, number>;
  destinations: GroupDestination[];
  firstCreatedAt: string | null;
  lastUpdatedAt: string | null;
}

export interface MigrationGroupWithRollup extends MigrationGroup {
  rollup: MigrationGroupRollup;
}

// THREE SIBLINGS, NOT TWO. `unknownGroup` counts migrations whose group id
// matches no group row — a delete/assign race, or the FD-ADR-030 §7 partition
// orphaning. It is NEVER folded into `ungrouped`: "references a group that no
// longer exists" is a different operator question from "has no group".
//
// groups + ungrouped + unknownGroup === totals. Asserted server-side and in the
// smoke tier; a rollup that does not add up is the failure most likely to ship
// unnoticed, because every individual number looks plausible on its own.
export interface MigrationGroupsListData {
  groups: MigrationGroupWithRollup[];
  ungrouped: MigrationGroupRollup;
  unknownGroup: MigrationGroupRollup;
  totals: MigrationGroupRollup;
}

export interface GroupMemberMigration {
  migrationId: string;
  migrationWave: string;
  migrationName: string;
  migrationType: string;
  deviceCount: number;
  createdAt: string;
  updatedAt: string;
  sourceTenantId: string;
  destTenantId: string;
  sourceTenantDomain: string;
  destTenantDomain: string;
  bprtStatus: string;
}

export interface GroupDeviceRollup {
  assigned: number;
  notStarted: number;
  inProgress: number;
  terminal: number;
  byOutcome: Record<string, number>;
  // Counted IN ADDITION to a lifecycle bucket — a held device is also
  // notStarted. Only the lifecycle buckets sum to `assigned`.
  held: number;
}

export interface MigrationGroupDetailData {
  migrationGroup: MigrationGroup;
  migrations: GroupMemberMigration[];
  rollup: MigrationGroupRollup;
  // `null` above the member ceiling or when the device read failed. When it is
  // null, `deviceRollupOmitted` explains why. NEVER render a partial count —
  // the server omits rather than truncates precisely so the UI does not have to
  // decide (FD-ADR-030 §4).
  deviceRollup: GroupDeviceRollup | null;
  deviceRollupOmitted: string | null;
}

export async function getMigrationGroups(): Promise<MigrationGroupsListData> {
  const envelope = await getJson<ApiEnvelope<MigrationGroupsListData>>('/v1/migration-groups');
  return envelope.data;
}

export async function getMigrationGroup(groupId: string): Promise<MigrationGroupDetailData> {
  const envelope = await getJson<ApiEnvelope<MigrationGroupDetailData>>(
    `/v1/migration-groups/${encodeURIComponent(groupId)}`,
  );
  return envelope.data;
}

export interface CreateMigrationGroupRequest {
  name: string;
  description?: string;
}

export async function createMigrationGroup(
  req: CreateMigrationGroupRequest,
): Promise<MigrationGroup> {
  const { body: envelope } = await postJsonWithStatus<
    ApiEnvelope<{ migrationGroup: MigrationGroup }>,
    CreateMigrationGroupRequest
  >('/v1/migration-groups', req);
  const group = envelope.data?.migrationGroup;
  if (!group?.migrationGroupId) {
    throw new Error(
      'POST /v1/migration-groups succeeded but response is missing data.migrationGroup',
    );
  }
  return group;
}

export interface UpdateMigrationGroupRequest {
  name?: string;
  description?: string;
}

export async function updateMigrationGroup(
  groupId: string,
  req: UpdateMigrationGroupRequest,
): Promise<MigrationGroup> {
  const { body: envelope } = await postJsonWithStatus<
    ApiEnvelope<{ migrationGroup: MigrationGroup }>,
    UpdateMigrationGroupRequest
  >(`/v1/migration-groups/${encodeURIComponent(groupId)}/update`, req);
  return envelope.data.migrationGroup;
}

export async function deleteMigrationGroup(groupId: string): Promise<void> {
  // 409 GROUP_NOT_EMPTY throws ApiError, carrying the count and ids in its
  // envelope — callers render that rather than a generic failure.
  await postJsonWithStatus<ApiEnvelope<{ deleted: boolean }>, Record<string, never>>(
    `/v1/migration-groups/${encodeURIComponent(groupId)}/delete`,
    {},
  );
}

export interface AssignGroupResult {
  migrationId: string;
  migrationGroupId: string | null;
  migrationGroupName: string | null;
  previousMigrationGroupId: string | null;
}

// `null` unassigns. Assigning an already-grouped migration MOVES it — that is
// what "exactly one group" means — and the result reports the previous group,
// because the audit value is the move rather than the set.
export async function assignMigrationGroup(
  migrationId: string,
  migrationGroupId: string | null,
): Promise<AssignGroupResult> {
  const { body: envelope } = await postJsonWithStatus<
    ApiEnvelope<AssignGroupResult>,
    { migrationGroupId: string | null }
  >(`/v1/migrations/${encodeURIComponent(migrationId)}/group`, { migrationGroupId });
  return envelope.data;
}
